import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import WebsiteLayout from '@/components/common/Layouts/WebsiteLayout';
import ApplyNow from '@/components/common/ApplyNow/ApplyNow';
import galleryApi from '@/services/api/galleryApi';
import { useSEO } from '@/hooks/useSEO';

const IMAGES_PER_PAGE = 12; // 3 rows × 4 columns

const Images = () => {
  // SEO Management
  const { seoData, loading: seoLoading } = useSEO('gallery-images', 'en');
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalImages, setTotalImages] = useState(0);
  const [error, setError] = useState(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    loadGalleryImages(1);
  }, []);

  const loadGalleryImages = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const response = await galleryApi.getPublicGalleryByPageSlug('gallery_images', {
        page,
        limit: IMAGES_PER_PAGE
      });
      
      if (response.success && response.data) {
        const newImages = response.data;
        
        if (page === 1) {
          setGalleryImages(newImages);
          setDisplayedImages(newImages);
        } else {
          setGalleryImages(prev => [...prev, ...newImages]);
          setDisplayedImages(prev => [...prev, ...newImages]);
        }
        
        // Check pagination info if available
        if (response.pagination) {
          setTotalImages(response.pagination.total);
          setHasMore(response.pagination.hasNext);
        } else {
          // If no pagination info, assume we got all images
          setHasMore(false);
          setTotalImages(newImages.length);
        }
        
        setCurrentPage(page);
      }
    } catch (err) {
      console.error('Error loading gallery images:', err);
      setError('Failed to load gallery images. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreImages = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadGalleryImages(currentPage + 1);
  }, [hasMore, loadingMore, currentPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading || !hasMore || loadingMore) return;

    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMoreImages();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMoreImages]);

  const filteredImages = displayedImages;

  const openLightbox = (image) => {
    // Find the image in the full gallery array for proper navigation
    const imageInGallery = galleryImages.find(img => img.id === image.id);
    setSelectedImage(imageInGallery);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction) => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % galleryImages.length;
    } else {
      newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    }
    
    setSelectedImage(galleryImages[newIndex]);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateImage('next');
      if (e.key === 'ArrowLeft') navigateImage('prev');
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, galleryImages]);

  if (loading) {
    return (
      <WebsiteLayout className="pt-16 lg:pt-20">
        <ApplyNow />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-orange-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Loading Gallery
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we load our photo gallery...
            </p>
          </div>
        </div>
      </WebsiteLayout>
    );
  }

  return (
    <WebsiteLayout className="pt-16 lg:pt-20">
      <ApplyNow />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Training facility"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <nav className="flex justify-center mb-4" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li className="text-gray-400">/</li>
                <li>
                  <span className="text-gray-300">Gallery</span>
                </li>
                <li className="text-gray-400">/</li>
                <li className="text-white font-medium">Images</li>
              </ol>
            </nav>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Photo Gallery
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed mb-8">
              Take a visual tour of our state-of-the-art training facility, modern equipment fleet, and successful training programs.
            </p>

            <div className="flex justify-center space-x-4">
              <Link
                to="/gallery/videos"
                className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-4-4v8m-4-4h8m-4-4v8" />
                </svg>
                Watch Videos
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Gallery Grid */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No images found</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try selecting a different category.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Gallery
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Showing {displayedImages.length} {totalImages > 0 ? `of ${totalImages}` : ''} image{displayedImages.length !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="group cursor-pointer bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
                    onClick={() => openLightbox(image)}
                  >
                    <div className="relative overflow-hidden aspect-square bg-gray-200 dark:bg-gray-700">
                      <img
                        src={image.media_url}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      {/* Image Number Badge */}
                      <div className="absolute top-3 left-3 bg-black/60 text-white px-2 py-1 rounded text-sm font-medium">
                        {index + 1} {totalImages > 0 ? `/ ${totalImages}` : ''}
                      </div>
                    </div>
                    
                    <div className="p-4 flex flex-col flex-grow">
                      <div className="group/title relative mb-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-orange-500 transition-colors truncate">
                          {image.title}
                        </h3>
                        <div className="absolute left-0 top-full z-50 hidden group-hover/title:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-normal max-w-xs break-words mt-1">
                          {image.title}
                        </div>
                      </div>
                      <div className="group/caption relative flex-grow">
                        <p className="text-gray-600 dark:text-gray-400 text-xs line-clamp-2">
                          {image.caption || 'No description'}
                        </p>
                        {image.caption && image.caption.length > 100 && (
                          <div className="absolute left-0 top-full z-50 hidden group-hover/caption:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-normal max-w-xs break-words mt-1">
                            {image.caption}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Loading indicator and intersection observer target */}
              {hasMore && (
                <div ref={loadMoreRef} className="flex justify-center items-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-500"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Loading more images...
                    </p>
                  </div>
                </div>
              )}

              {/* End of gallery message */}
              {!hasMore && displayedImages.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    You've reached the end of the gallery
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
          <div className="relative max-w-7xl max-h-screen w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Navigation Buttons */}
            {filteredImages.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 z-50 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 z-50 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Image Content */}
            <div className="flex flex-col items-center max-w-full max-h-full">
              <div className="relative max-w-full max-h-[80vh] mb-4">
                <img
                  src={selectedImage.media_url}
                  alt={selectedImage.title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                />
              </div>
              
              {/* Image Info */}
              <div className="text-center text-white bg-black/50 rounded-lg px-6 py-4 max-w-2xl">
                <h3 className="text-xl font-bold mb-2">{selectedImage.title}</h3>
                <p className="text-gray-300">{selectedImage.caption || 'No description'}</p>
                <div className="mt-4 text-sm text-gray-400">
                  Image {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} of {totalImages > 0 ? totalImages : galleryImages.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Be Part of Our Success Story?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Join our training programs and become part of our gallery of successful graduates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/apply"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-bold text-lg rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Apply Now
              <svg className="ml-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/gallery/videos"
              className="inline-flex items-center px-8 py-4 border-2 border-orange-500 text-orange-500 font-semibold text-lg rounded-lg hover:bg-orange-500 hover:text-white transition-all duration-300"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-4-4v8m-4-4h8m-4-4v8" />
              </svg>
              Watch Videos
            </Link>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default Images;
